import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { ScorecardApi, type Scorecard } from "../api/scorecard";
import { NO_DATE, isoDate, promoLabel, scorecardText } from "../utils/scorecardText";
import StatsPanel from "./StatsPanel";
import logger from "../analytics/logger";

export default function WeeklyScorecard() {
  const [data, setData] = useState<Scorecard | null>(null);
  const toast = useToast();

  useEffect(() => {
    ScorecardApi.get()
      .then(setData)
      .catch((e) => logger.error("welcome_scorecard_failed", e));
  }, []);

  async function copy() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(scorecardText(data));
      toast({ title: "Scorecard copied", status: "success", duration: 2000 });
    } catch (e) {
      logger.error("scorecard_copy_failed", e);
      toast({ title: "Copy failed", status: "error", duration: 3000 });
    }
  }

  if (!data) {
    return (
      <Box>
        <Heading size="md" mb={3}>
          Weekly Scorecard
        </Heading>
        <Text color="gray.500">Loading…</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="md" mb={3}>
        Weekly Scorecard
      </Heading>
      <Box
        borderWidth="1px"
        p={4}
        minW="280px"
        flex="1 1 280px"
      >
      <StatsPanel
        sections={[
          {
            title: "Last 7 days",
            entries: [
              { label: "Signups", value: data.totals.signups },
              { label: "Creators verified", value: data.totals.creator_verifications },
              { label: "Works certified", value: data.totals.work_certifications },
            ],
          },
        ]}
      />

      <Table size="sm" variant="simple" mb={3}>
        <Thead>
          <Tr>
            <Th>Promo</Th>
            <Th isNumeric>Signups</Th>
            <Th isNumeric>Verifications</Th>
            <Th>Start</Th>
            <Th>End</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.promos.map((p) => (
            <Tr key={p.origin_event_id ?? "unattributed"}>
              <Td fontWeight={p.is_house_default_now ? "semibold" : "normal"}>
                {promoLabel(p)}
                {p.is_house_default_now ? " (live)" : ""}
              </Td>
              <Td isNumeric>{p.signups.toLocaleString()}</Td>
              <Td isNumeric>{p.verifications.toLocaleString()}</Td>
              <Td>{p.is_unattributed ? NO_DATE : isoDate(p.house_default_start_at)}</Td>
              <Td>{p.is_unattributed ? NO_DATE : isoDate(p.house_default_end_at)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

        {data.next_promo ?
            <Text color="gray.700" mb={3}>
                `Next promo is "${promoLabel(data.next_promo)}", starting on ${isoDate(
                data.next_promo.house_default_start_at,)}`
            </Text>
        : null}      

      <Button size="sm" onClick={copy}>
        Copy
      </Button>
      </Box>
    </Box>
  );
}
